import React from 'react';

/* ── Constants ───────────────────────────────────────────────── */
const HOURLY_RANGE_OPTIONS = [
    { label: '7d', days: 7 },
    { label: '14d', days: 14 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
];
const HOURLY_CHART_TYPES = [
    { key: 'bar', icon: '▌▌', label: 'Bar' },
    { key: 'line', icon: '╱', label: 'Line' },
    { key: 'area', icon: '◿', label: 'Area' },
    { key: 'polar', icon: '◎', label: 'Clock' },
];

/* ── Label helpers ───────────────────────────────────────────── */
const hourLabel = (h) => {
    if (h === 0) return '12am';
    if (h === 12) return '12pm';
    return h < 12 ? `${h}am` : `${h - 12}pm`;
};

// AM/PM period colour tint for bars
const hourColor = (h) => {
    if (h >= 0 && h < 6) return '#312e81'; // midnight-dawn  (very dark)
    if (h >= 6 && h < 12) return '#4f46e5'; // morning        (indigo)
    if (h >= 12 && h < 18) return '#6366f1'; // afternoon      (bright indigo)
    return '#818cf8';                          // evening        (light indigo)
};

/* ── Shared ctrl button style ────────────────────────────────── */
const ctrlBtn = (active) => ({
    padding: '4px 13px', borderRadius: 8, fontSize: 12, fontWeight: 700,
    border: 'none', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.18s',
    background: active ? 'rgba(99,102,241,0.22)' : 'rgba(255,255,255,0.04)',
    color: active ? '#a5b4fc' : '#475569',
    outline: active ? '1px solid rgba(99,102,241,0.35)' : '1px solid transparent',
});

/* ── Build full 24-slot scaffold from API rows ───────────────── */
const buildHours = (data) => {
    const slots = Array.from({ length: 24 }, (_, h) => ({ hour: h, clicks: 0 }));
    (data || []).forEach(row => {
        const h = parseInt(row.hour);
        if (h >= 0 && h < 24) slots[h].clicks = parseInt(row.clicks) || 0;
    });
    return slots;
};

/* ── Tooltip ─────────────────────────────────────────────────── */
const TooltipEl = ({ tooltip }) => {
    if (!tooltip) return null;
    return (
        <div style={{
            position: 'absolute', left: tooltip.x, top: tooltip.y - 52,
            transform: 'translateX(-50%)',
            background: '#1e2a45', border: '1px solid rgba(99,102,241,0.35)',
            borderRadius: 8, padding: '6px 12px',
            fontSize: 12, fontWeight: 700, color: '#e2e8f0',
            pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 10,
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}>
            <span style={{ color: '#818cf8' }}>{hourLabel(tooltip.hour)}</span><br />
            {tooltip.clicks} click{tooltip.clicks !== 1 ? 's' : ''}
        </div>
    );
};

/* ── Bar / Line / Area (shared SVG canvas) ───────────────────── */
const LinearChart = ({ hours, chartType }) => {
    const [tooltip, setTooltip] = React.useState(null);

    const n = 24;
    const maxClicks = Math.max(...hours.map(d => d.clicks), 1);
    const W = 800, H = 220, padL = 44, padR = 12, padT = 20, padB = 50;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;
    const xStep = chartW / n;
    const barW = Math.max(4, xStep - 4);

    const px = (i) => padL + i * xStep + xStep / 2;
    const py = (v) => padT + chartH * (1 - v / maxClicks);

    const points = hours.map((d, i) => `${px(i)},${py(d.clicks)}`).join(' ');
    const areaPath = `M${px(0)},${padT + chartH} ` +
        hours.map((d, i) => `L${px(i)},${py(d.clicks)}`).join(' ') +
        ` L${px(23)},${padT + chartH} Z`;

    const yLines = [0.25, 0.5, 0.75, 1].map(f => ({
        y: padT + chartH * (1 - f), label: Math.round(maxClicks * f),
    }));

    // Show every 2nd hour label (12 labels)
    const xLabels = hours.filter((_, i) => i % 2 === 0);

    const tip = (e, d) => {
        const r = e.currentTarget.closest('svg').getBoundingClientRect();
        const i = d.hour;
        setTooltip({ x: px(i) * (r.width / W), y: py(d.clicks) * (r.height / H), hour: d.hour, clicks: d.clicks });
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <TooltipEl tooltip={tooltip} />
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
                <defs>
                    <linearGradient id="hcAreaGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity="0.45" />
                        <stop offset="100%" stopColor="#6366f1" stopOpacity="0.03" />
                    </linearGradient>
                    {/* AM/PM period bands */}
                    <linearGradient id="hcBandGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity="0.04" />
                        <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
                    </linearGradient>
                </defs>

                {/* Period background bands */}
                {[
                    { start: 0, end: 6, label: 'Night' },
                    { start: 6, end: 12, label: 'Morning' },
                    { start: 12, end: 18, label: 'Afternoon' },
                    { start: 18, end: 24, label: 'Evening' },
                ].map(band => {
                    const bx = padL + band.start * xStep;
                    const bw = (band.end - band.start) * xStep;
                    const isEven = [0, 12].includes(band.start);
                    return (
                        <g key={band.label}>
                            <rect x={bx} y={padT} width={bw} height={chartH}
                                fill={isEven ? 'rgba(99,102,241,0.04)' : 'transparent'} />
                            <text x={bx + bw / 2} y={H - 8} textAnchor="middle"
                                fill="#2d3748" fontSize="9" fontFamily="Inter, sans-serif">{band.label}</text>
                        </g>
                    );
                })}

                {/* Y-grid */}
                {yLines.map((gl, i) => (
                    <g key={i}>
                        <line x1={padL} y1={gl.y} x2={W - padR} y2={gl.y}
                            stroke="rgba(99,102,241,0.1)" strokeWidth="1" strokeDasharray="4 4" />
                        <text x={padL - 6} y={gl.y + 4} textAnchor="end"
                            fill="#475569" fontSize="10" fontFamily="Inter, sans-serif">{gl.label}</text>
                    </g>
                ))}

                {/* ── Bar ── */}
                {chartType === 'bar' && hours.map((d, i) => {
                    const bh = Math.max(2, (d.clicks / maxClicks) * chartH);
                    const bx = padL + i * xStep + (xStep - barW) / 2;
                    const by = padT + chartH - bh;
                    return (
                        <g key={d.hour}>
                            <rect x={bx} y={by} width={barW} height={bh} rx={3}
                                fill={d.clicks === 0 ? 'rgba(51,65,85,0.35)' : hourColor(d.hour)} />
                            <rect x={padL + i * xStep} y={padT} width={xStep} height={chartH}
                                fill="transparent" style={{ cursor: 'crosshair' }}
                                onMouseEnter={e => tip(e, d)} onMouseLeave={() => setTooltip(null)} />
                        </g>
                    );
                })}

                {/* ── Area ── */}
                {chartType === 'area' && (
                    <>
                        <path d={areaPath} fill="url(#hcAreaGrad)" />
                        <polyline points={points} fill="none"
                            stroke="#6366f1" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                        {hours.map((d, i) => (
                            <circle key={d.hour} cx={px(i)} cy={py(d.clicks)} r={4}
                                fill="#6366f1" stroke="#1e2a45" strokeWidth="2" style={{ cursor: 'crosshair' }}
                                onMouseEnter={e => tip(e, d)} onMouseLeave={() => setTooltip(null)} />
                        ))}
                    </>
                )}

                {/* ── Line ── */}
                {chartType === 'line' && (
                    <>
                        <polyline points={points} fill="none"
                            stroke="#818cf8" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                        {hours.map((d, i) => (
                            <circle key={d.hour} cx={px(i)} cy={py(d.clicks)} r={3.5}
                                fill={d.clicks === 0 ? '#334155' : '#6366f1'} stroke="#0a0f1e" strokeWidth="1.5"
                                style={{ cursor: 'crosshair' }}
                                onMouseEnter={e => tip(e, d)} onMouseLeave={() => setTooltip(null)} />
                        ))}
                    </>
                )}

                {/* X-axis line */}
                <line x1={padL} y1={padT + chartH} x2={W - padR} y2={padT + chartH}
                    stroke="rgba(99,102,241,0.2)" strokeWidth="1" />

                {/* X-axis hour labels */}
                {xLabels.map(d => (
                    <text key={d.hour} x={px(d.hour)} y={padT + chartH + 14} textAnchor="middle"
                        fill="#475569" fontSize="9.5" fontFamily="Inter, sans-serif">{hourLabel(d.hour)}</text>
                ))}
            </svg>
        </div>
    );
};

/* ── Polar / Clock chart ─────────────────────────────────────── */
const PolarChart = ({ hours }) => {
    const [tooltip, setTooltip] = React.useState(null);

    const cx = 200, cy = 200, r0 = 40, r1 = 160;
    const maxClicks = Math.max(...hours.map(d => d.clicks), 1);
    const W = 400, H = 400;

    // Each hour = 15° (360/24). Start at top (-90°).
    const toRad = (deg) => (deg * Math.PI) / 180;
    const sliceAngle = 360 / 24;

    const arcPath = (h, clicks) => {
        const angleStart = toRad(-90 + h * sliceAngle);
        const angleEnd = toRad(-90 + (h + 1) * sliceAngle - 0.5);
        const rOuter = r0 + Math.max(2, (clicks / maxClicks) * (r1 - r0));

        const x1 = cx + r0 * Math.cos(angleStart);
        const y1 = cy + r0 * Math.sin(angleStart);
        const x2 = cx + rOuter * Math.cos(angleStart);
        const y2 = cy + rOuter * Math.sin(angleStart);
        const x3 = cx + rOuter * Math.cos(angleEnd);
        const y3 = cy + rOuter * Math.sin(angleEnd);
        const x4 = cx + r0 * Math.cos(angleEnd);
        const y4 = cy + r0 * Math.sin(angleEnd);

        return `M${x1},${y1} L${x2},${y2} A${rOuter},${rOuter},0,0,1,${x3},${y3} L${x4},${y4} A${r0},${r0},0,0,0,${x1},${y1} Z`;
    };

    // Hour label position (beyond r1)
    const labelPos = (h) => {
        const angle = toRad(-90 + (h + 0.5) * sliceAngle);
        return { x: cx + (r1 + 18) * Math.cos(angle), y: cy + (r1 + 18) * Math.sin(angle) };
    };

    // Tooltip position at midpoint of arc
    const midPos = (h, clicks) => {
        const rOuter = r0 + Math.max(2, (clicks / maxClicks) * (r1 - r0));
        const angle = toRad(-90 + (h + 0.5) * sliceAngle);
        const rMid = (r0 + rOuter) / 2;
        return { x: cx + rMid * Math.cos(angle), y: cy + rMid * Math.sin(angle) };
    };

    // Show labels every 3 hours (0, 3, 6, 9, 12, 15, 18, 21)
    const showLabel = (h) => h % 3 === 0;

    return (
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
            {tooltip && (
                <div style={{
                    position: 'absolute',
                    left: tooltip.svgX, top: tooltip.svgY - 48,
                    transform: 'translateX(-50%)',
                    background: '#1e2a45', border: '1px solid rgba(99,102,241,0.35)',
                    borderRadius: 8, padding: '6px 12px',
                    fontSize: 12, fontWeight: 700, color: '#e2e8f0',
                    pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 10,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                }}>
                    <span style={{ color: '#818cf8' }}>{hourLabel(tooltip.hour)}</span><br />
                    {tooltip.clicks} click{tooltip.clicks !== 1 ? 's' : ''}
                </div>
            )}
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxWidth: 380, height: 'auto', display: 'block' }}>
                {/* Background rings */}
                {[0.25, 0.5, 0.75, 1].map(f => (
                    <circle key={f} cx={cx} cy={cy} r={r0 + f * (r1 - r0)}
                        fill="none" stroke="rgba(99,102,241,0.08)" strokeWidth="1" strokeDasharray="4 4" />
                ))}
                {/* Inner hub */}
                <circle cx={cx} cy={cy} r={r0} fill="rgba(15,23,42,0.8)" stroke="rgba(99,102,241,0.2)" strokeWidth="1" />

                {/* Arcs */}
                {hours.map(d => (
                    <path key={d.hour} d={arcPath(d.hour, d.clicks)}
                        fill={d.clicks === 0 ? 'rgba(51,65,85,0.2)' : hourColor(d.hour)}
                        opacity={d.clicks === 0 ? 1 : 0.85}
                        style={{ cursor: 'crosshair' }}
                        onMouseEnter={e => {
                            const { x, y } = midPos(d.hour, d.clicks);
                            const svgRect = e.currentTarget.closest('svg').getBoundingClientRect();
                            setTooltip({
                                svgX: x * (svgRect.width / W),
                                svgY: y * (svgRect.height / H),
                                hour: d.hour, clicks: d.clicks,
                            });
                        }}
                        onMouseLeave={() => setTooltip(null)}
                    />
                ))}

                {/* Hour labels every 3h */}
                {hours.map(d => {
                    if (!showLabel(d.hour)) return null;
                    const { x, y } = labelPos(d.hour);
                    return (
                        <text key={d.hour} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
                            fill="#475569" fontSize="10" fontFamily="Inter, sans-serif">
                            {hourLabel(d.hour)}
                        </text>
                    );
                })}

                {/* Centre text: peak hour */}
                {(() => {
                    const peak = hours.reduce((best, d) => d.clicks > best.clicks ? d : best, hours[0]);
                    return peak.clicks > 0 ? (
                        <>
                            <text x={cx} y={cy - 10} textAnchor="middle" fill="#a5b4fc" fontSize="11" fontFamily="Inter, sans-serif" fontWeight="700">Peak</text>
                            <text x={cx} y={cy + 6} textAnchor="middle" fill="#f1f5f9" fontSize="14" fontFamily="Inter, sans-serif" fontWeight="900">{hourLabel(peak.hour)}</text>
                            <text x={cx} y={cy + 22} textAnchor="middle" fill="#475569" fontSize="9" fontFamily="Inter, sans-serif">{peak.clicks} clicks</text>
                        </>
                    ) : (
                        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fill="#334155" fontSize="11" fontFamily="Inter, sans-serif">No data</text>
                    );
                })()}
            </svg>
        </div>
    );
};

/* ── Main export ─────────────────────────────────────────────── */
const HourlyChart = ({ data, hourlyRange = 30, setHourlyRange }) => {
    const [chartType, setChartType] = React.useState('bar');

    const hours = buildHours(data);
    const noData = !data || data.length === 0;

    return (
        <div>
            {/* Controls */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                {/* Day range */}
                <div style={{ display: 'flex', gap: 4 }}>
                    {HOURLY_RANGE_OPTIONS.map(r => (
                        <button key={r.days} onClick={() => setHourlyRange && setHourlyRange(r.days)} style={ctrlBtn(hourlyRange === r.days)}>
                            {r.label}
                        </button>
                    ))}
                </div>
                {/* Chart type */}
                <div style={{ display: 'flex', gap: 4 }}>
                    {HOURLY_CHART_TYPES.map(ct => (
                        <button key={ct.key} onClick={() => setChartType(ct.key)} title={ct.label} style={ctrlBtn(chartType === ct.key)}>
                            {ct.icon} {ct.label}
                        </button>
                    ))}
                </div>
            </div>

            {noData ? (
                <div style={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, color: '#475569' }}>
                    <span style={{ fontSize: 28 }}>🕐</span>
                    <span style={{ fontSize: 13 }}>No hourly click data for the last {hourlyRange} days</span>
                </div>
            ) : (
                <>
                    {(chartType === 'bar' || chartType === 'line' || chartType === 'area') && (
                        <LinearChart hours={hours} chartType={chartType} />
                    )}
                    {chartType === 'polar' && <PolarChart hours={hours} />}
                </>
            )}
        </div>
    );
};

export default HourlyChart;
